import { Injectable, signal } from '@angular/core';
import { ActivityRecordService } from '@shared/services/activity-record/activity-record.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { SummaryPeriod } from '@shared/models/summary-period';
import { DateTime } from 'luxon';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { ActivitySummaryResponse } from '@shared/models/activity-summary/activity-summary.response';
import { AppSettings } from '@shared/models/settings/app-settings';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { TableView } from './table-view';
import { FlightTotalsService } from '../services/flight-totals.service';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';

interface PrevSelectedPeriod {
  date: SummaryPeriod;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})

export class FlightsService {
  constructor(
    public activityRecordService: ActivityRecordService,
    private flightTotalsService: FlightTotalsService,
    public signalService: SignalsService,
    private errorLogService: ErrorLogService
  ) { }

  _forceRefresh = signal<boolean>(false);

  readonly STORAGE_KEYS = {
    prevSelectedPeriod: 'calendar.prevSelectedMonth',
  };

  private _selectedMonth = new BehaviorSubject<SummaryPeriod | null>(null);
  selectedMonth$ = this._selectedMonth.asObservable();

  private settingsChangedSubject = new BehaviorSubject<AppSettings>(this.getCurrentAppSettings());
  settingsChangedAction$ = this.settingsChangedSubject.asObservable();

  empNumber$ = this.signalService.employeeNumber$;
  activityRecordList$ = this.getActivityRecords();

  getIntialDate(): SummaryPeriod {
    const initialPeriod: SummaryPeriod = {
      year: DateTime.now().year,
      period: DateTime.now().month,
    };

    const prevSelectedMonth = localStorage.getItem(
      this.STORAGE_KEYS.prevSelectedPeriod
    );

    if (prevSelectedMonth) {
      const selectedMonth = JSON.parse(prevSelectedMonth) as PrevSelectedPeriod;
      const age =
        DateTime.fromISO(selectedMonth.timestamp).diffNow('days').days * -1;
      if (age < 2) {
        return selectedMonth.date;
      }
    }

    return initialPeriod;
  }

  saveSelectedMonth(date: SummaryPeriod): void {
    const selectedMonth: PrevSelectedPeriod = {
      date,
      timestamp: DateTime.now().toISO(),
    };
    localStorage.setItem(
      this.STORAGE_KEYS.prevSelectedPeriod,
      JSON.stringify(selectedMonth)
    );
  }

  /** Updates selected month observable. Set when the calendar month is changed. */
  setSelectedMonth(date: SummaryPeriod): void {
    this.saveSelectedMonth(date);
    this._selectedMonth.next(date);
  }

  getActivityRecords(): Observable<RequestState<ActivitySummaryResponse>> {
    return this.createTableView().pipe(
      map((tableView) => {
        if (tableView.hasError) {
          //this.showErrorAlert('Create tableView', tableView.status);
          console.error('Error: TableView getActivityRecords() ', tableView.status);
        }

        if (!tableView || !tableView.response) {
          return new RequestState<ActivitySummaryResponse>(
            RequestStatus.IsCompleteNoResponse,
            null
          );
        }

        
        if (tableView.response && !tableView.isLoading) {
          const summaryRecordsStirng = JSON.stringify(tableView.response);
          const summaryRecordsCopy: ActivitySummaryResponse = JSON.parse(summaryRecordsStirng);
          summaryRecordsCopy.sequences = this.sortSequences(tableView.response.sequences);
          this.signalService.isLoading$.set(false);
          return tableView.into<ActivitySummaryResponse>(summaryRecordsCopy);
        }

        return tableView.into<ActivitySummaryResponse>(undefined);
      })
    );

  }

  /**
   * Creates a table view for displaying flight sequences based on the current month's activity records.
   * 
   * This method retrieves activity records for the current month using the employee number,
   * transforms them into sequence events, and packages them into a TableView object along
   * with a timestamp.
   * 
   * @returns An Observable that emits a RequestState containing the TableView.
   * The TableView includes sequences and a timestamp from the activity record.
   * 
   * @throws Logs an error to console if the activity record retrieval fails
   */
  createTableView(): Observable<RequestState<TableView>> {
    return this.empNumber$.pipe(
      switchMap((empNumber) => {

        return this.getActivityRecordCurrentMonth(empNumber)
          .pipe(
            map((current) => {
              if (current.hasError) {
                console.error('Error: createTableView() ', current.status);
              }

              const currentPeriodActivityRecord = current.response;
              const currentPeriodSeqEvents = this.createSequenceEvent(currentPeriodActivityRecord);

              const tableView: TableView = {
                sequences: currentPeriodSeqEvents,
                timeStamp: DateTime.fromISO(currentPeriodActivityRecord?.timestamp || '')
              }
              return current.into<TableView>(tableView)
            })
          )
      })
    )
  }

  // Sorts sequences by date (descending).
  private sortSequences(sequences: Sequences[]): Sequences[] {
    let sorted = sequences.sort((a, b) => {
      const aDateTime = DateTime.fromISO(a.originationDate);
      const bDateTime = DateTime.fromISO(b.originationDate);

      return bDateTime.diff(aDateTime, 'hours').hours;
    });

    return sorted;
  }

  /**
   * Forces a refresh of the flight data.
   * This method triggers a refresh by:
   * 1. Setting the force refresh flag to true
   * 2. Notifying the flight totals service of the refresh
   * 3. Re-setting the currently selected month if one exists to new month.
   */
  forceRefresh(): void {
    this._forceRefresh.set(true);
    const selectedMonth = this._selectedMonth.getValue();
    this.flightTotalsService._isRefresh.next(true);

    if (selectedMonth) {
      this.setSelectedMonth(selectedMonth);
    }
  }

  getActivityRecordCurrentMonth(empNumber: string): Observable<
    RequestState<ActivitySummaryResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {
        if (this._forceRefresh()) {
          this._forceRefresh.set(false);

          return this.activityRecordService
            .getSummaryRecords(
              empNumber,
              selectedMonth!.year,
              selectedMonth!.period,
              !this._forceRefresh()
            )
            .pipe(
              tap((activityRecordState) => {
                if(activityRecordState && !activityRecordState.isLoading) {
                  console.log("%cFlight Records refreshed", "color: lightgreen");
                }
                if (activityRecordState.hasError) {
                  this.errorLogService.logError(`Flight Service: getActivityRecordCurrentMonth() ${activityRecordState.status}`);
                }
              })
            );
        } else {
          return this.activityRecordService
            .getSummaryRecords(
              empNumber,
              selectedMonth!.year,
              selectedMonth!.period
            )
            .pipe(
              tap((activityRecordState) => {
                console.log("%cFlight Records updated", "color: lightgreen");
                if (activityRecordState.hasError) {
                  this.errorLogService.logError(`Flight Service: getActivityRecordCurrentMonth() ${activityRecordState.status}`);
                }
              })
            );
        }
      })
    );
  }

  createSequenceEvent(summaryPeriodResponse?: any | null): Sequences[] {
    const seqEvents: Sequences[] = [];
    if (!summaryPeriodResponse) {
      return [];
    }
    for (const sequence of summaryPeriodResponse.sequences) {
      seqEvents.push(sequence)
    }
    return seqEvents;
  }

  getSelectedPeriod(
    summaryPeriod: SummaryPeriod,
    adjacent?: string
  ): SummaryPeriod {
    if (adjacent == 'prev') {
      if (summaryPeriod?.period === 1) {
        const updatedPeriod = {
          year: summaryPeriod.year - 1,
          period: 12,
        };
        return updatedPeriod;
      }
      const updatedPeriod = {
        year: summaryPeriod.year,
        period: summaryPeriod.period - 1,
      };
      return updatedPeriod;
    } else {
      if (summaryPeriod.period === 12) {
        const updatedPeriod = {
          year: summaryPeriod.year + 1,
          period: 1,
        };
        return updatedPeriod;
      }
      const updatedPeriod = {
        year: summaryPeriod.year,
        period: summaryPeriod.period + 1,
      };
      return updatedPeriod;
    }
  }

  getCurrentAppSettings(): AppSettings {
    const appSettings = localStorage.getItem('appSettings');
    if (appSettings) {
      const settings = JSON.parse(appSettings);
      return settings;
    } else {
      return this.getDefaultAppSettings();
    }

    //Use this for endpoint when it is available
    // this.settingsService.postUserSettings().subscribe( (settings) => {
    //   if (!settings) {
    //   }
    //   return settings.response;
    // })
    // return this.getDefaultAppSettings();
  }

  private getDefaultAppSettings(): AppSettings {
    return {
      dashboardViewDefault: 0,
      dateTimeFormatDefault: 'local',
      filter: {
        flights: true,
        deadhead: true,
        cancelled: true,
      },
      sortOrder: 0,
    };
  }

  onChangeOfFilterSetting(appSettings: AppSettings): void {
    this.settingsChangedSubject.next(appSettings);
  }

  // private showErrorAlert(from: string, status: number): void {
  //   const statusCode = RequestStatus[status];
  //   this.messageService.add({
  //     severity: 'error',
  //     detail: `Error for: ${from} - ${statusCode}`,
  //   });
  // }

  clearSummaryRecordCache(): void {
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      if (key.includes('summaryRecord')) {
        localStorage.removeItem(key);
      }
    }
  }
}

